import React, { useState, useEffect } from "react";
import { 
  Table, 
  Card, 
  Typography, 
  Tag, 
  Button, 
  Space, 
  Tooltip,
  Modal,
  message,
  Input,
  Form,
  Select
} from "antd";
import { 
  EyeOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  PlusOutlined,
  CopyOutlined,
  LinkOutlined
} from "@ant-design/icons";
import { GuestmapTemplate, AdvertGuestmapAssignment } from "./types/Guestmap";

const { Title, Text } = Typography;
const { Search } = Input;
const { Option } = Select;

export const GuestmapTemplates: React.FC = () => {
  const [templates, setTemplates] = useState<GuestmapTemplate[]>([]);
  const [advertAssignments, setAdvertAssignments] = useState<AdvertGuestmapAssignment[]>([]);
  const [loading, setLoading] = useState(false);
  const [previewModalVisible, setPreviewModalVisible] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<GuestmapTemplate | null>(null);
  const [assignmentModalVisible, setAssignmentModalVisible] = useState(false);
  const [selectedTemplateForAssignment, setSelectedTemplateForAssignment] = useState<GuestmapTemplate | null>(null);

  useEffect(() => {
    loadTemplates();
    loadAdvertAssignments();
  }, []);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      
      const response = await fetch('/?type=8847379247&tx_buchungsmanager_frontend[action]=getGuestmapTemplates');
      
      if (response.ok) {
        const data = await response.json();
        setTemplates(data.templates || []);
      } else {
        message.error('Fehler beim Laden der Vorlagen');
      }
    } catch (error) {
      console.error('Fehler beim Laden der Vorlagen:', error);
      message.error('Fehler beim Laden der Vorlagen');
    } finally {
      setLoading(false);
    }
  };

  const loadAdvertAssignments = async () => {
    try {
      const response = await fetch('/?type=8847379247&tx_buchungsmanager_frontend[action]=getAdvertAssignments');
      
      if (response.ok) {
        const data = await response.json();
        setAdvertAssignments(data.assignments || []);
      }
    } catch (error) {
      console.error('Fehler beim Laden der Advert-Zuordnungen:', error);
    }
  };

  const handlePreview = (template: GuestmapTemplate) => {
    setSelectedTemplate(template);
    setPreviewModalVisible(true);
  };

  const handleDelete = async (templateId: string) => {
    try {
      const response = await fetch('/?type=8847379247&tx_buchungsmanager_frontend[action]=deleteGuestmapTemplate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ templateId })
      });

      if (response.ok) {
        message.success('Vorlage erfolgreich gelöscht');
        loadTemplates();
      } else {
        message.error('Fehler beim Löschen der Vorlage');
      }
    } catch (error) {
      console.error('Fehler beim Löschen:', error);
      message.error('Fehler beim Löschen der Vorlage');
    }
  };

  const handleCopy = async (templateId: string) => {
    try {
      const response = await fetch('/?type=8847379247&tx_buchungsmanager_frontend[action]=copyGuestmapTemplate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ templateId })
      });

      if (response.ok) {
        message.success('Vorlage erfolgreich kopiert');
        loadTemplates();
      } else {
        message.error('Fehler beim Kopieren der Vorlage');
      }
    } catch (error) {
      console.error('Fehler beim Kopieren:', error);
      message.error('Fehler beim Kopieren der Vorlage');
    }
  };

  const handleAssignToAdvert = (template: GuestmapTemplate) => {
    setSelectedTemplateForAssignment(template);
    setAssignmentModalVisible(true);
  };

  const handleAssignmentSubmit = async (values: any) => {
    try {
      const response = await fetch('/?type=8847379247&tx_buchungsmanager_frontend[action]=assignTemplateToAdvert', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          templateId: selectedTemplateForAssignment?.id,
          advertId: values.advertId
        })
      });

      if (response.ok) {
        message.success('Vorlage erfolgreich zugewiesen');
        setAssignmentModalVisible(false);
        loadAdvertAssignments();
      } else {
        message.error('Fehler beim Zuweisen der Vorlage');
      }
    } catch (error) {
      console.error('Fehler beim Zuweisen:', error);
      message.error('Fehler beim Zuweisen der Vorlage');
    }
  };

  const columns = [
    {
      title: 'Name',
      key: 'name',
      render: (record: GuestmapTemplate) => (
        <Text strong>{record.name}</Text>
      ),
    },
    {
      title: 'Beschreibung',
      key: 'description',
      render: (record: GuestmapTemplate) => (
        <Text>{record.description || '-'}</Text>
      ),
    },
    {
      title: 'Gastgeber',
      key: 'host',
      render: (record: GuestmapTemplate) => (
        <Text>{record.host.name}</Text>
      ),
    },
    {
      title: 'Status',
      key: 'status',
      render: (record: GuestmapTemplate) => (
        <Tag color={record.isActive ? 'green' : 'red'}>
          {record.isActive ? 'Aktiv' : 'Inaktiv'}
        </Tag>
      ),
    },
    {
      title: 'Erstellt',
      key: 'createdAt',
      render: (record: GuestmapTemplate) => (
        <Text>{new Date(record.createdAt).toLocaleDateString('de-DE')}</Text>
      ),
    },
    {
      title: 'Aktionen',
      key: 'actions',
      render: (record: GuestmapTemplate) => (
        <Space>
          <Tooltip title="Vorschau">
            <Button 
              type="text" 
              icon={<EyeOutlined />} 
              onClick={() => handlePreview(record)}
            />
          </Tooltip>
          <Tooltip title="Bearbeiten">
            <Button 
              type="text" 
              icon={<EditOutlined />}
              onClick={() => window.location.hash = `#/digitale-gaestemappe/bearbeiten/${record.id}`}
            />
          </Tooltip>
          <Tooltip title="Kopieren">
            <Button 
              type="text" 
              icon={<CopyOutlined />}
              onClick={() => handleCopy(record.id)}
            />
          </Tooltip>
          <Tooltip title="Advert zuweisen">
            <Button 
              type="text" 
              icon={<LinkOutlined />}
              onClick={() => handleAssignToAdvert(record)}
            />
          </Tooltip>
          <Tooltip title="Löschen">
            <Button 
              type="text" 
              danger 
              icon={<DeleteOutlined />}
              onClick={() => handleDelete(record.id)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <div style={{ marginBottom: '24px' }}>
          <Title level={2}>
            <PlusOutlined style={{ marginRight: '8px' }} />
            Gästemappen-Vorlagen
          </Title>
          <Text type="secondary">
            Verwaltung der Gästemappen-Vorlagen und deren Zuordnung zu Adverts
          </Text>
        </div>

        <div style={{ marginBottom: '16px' }}>
          <Space>
            <Button 
              type="primary" 
              icon={<PlusOutlined />}
              onClick={() => window.location.hash = '#/digitale-gaestemappe'}
            >
              Neue Vorlage erstellen
            </Button>
            <Search
              placeholder="Vorlagen durchsuchen..."
              style={{ width: 300 }}
            />
          </Space>
        </div>

        <Table
          columns={columns}
          dataSource={templates}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => 
              `${range[0]}-${range[1]} von ${total} Vorlagen`,
          }}
        />
      </Card>

      {/* Vorschau Modal */}
      <Modal
        title="Vorlage Vorschau"
        open={previewModalVisible}
        onCancel={() => setPreviewModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setPreviewModalVisible(false)}>
            Schließen
          </Button>,
          <Button 
            key="edit" 
            type="primary"
            onClick={() => {
              if (selectedTemplate) {
                window.location.hash = `#/digitale-gaestemappe/bearbeiten/${selectedTemplate.id}`;
              }
              setPreviewModalVisible(false);
            }}
          >
            Bearbeiten
          </Button>
        ]}
        width={800}
      >
        {selectedTemplate && (
          <div>
            <h3>{selectedTemplate.name}</h3>
            {selectedTemplate.description && (
              <p><strong>Beschreibung:</strong> {selectedTemplate.description}</p>
            )}
            <p><strong>Gastgeber:</strong> {selectedTemplate.host.name}</p>
            <p><strong>Betrieb:</strong> {selectedTemplate.host.businessName}</p>
            <p><strong>Status:</strong> {selectedTemplate.isActive ? 'Aktiv' : 'Inaktiv'}</p>
            <p><strong>Erstellt:</strong> {new Date(selectedTemplate.createdAt).toLocaleDateString('de-DE')}</p>
          </div>
        )}
      </Modal>

      {/* Advert-Zuordnung Modal */}
      <Modal
        title="Vorlage Advert zuweisen"
        open={assignmentModalVisible}
        onCancel={() => setAssignmentModalVisible(false)}
        footer={null}
      >
        <Form onFinish={handleAssignmentSubmit}>
          <Form.Item
            name="advertId"
            label="Advert auswählen"
            rules={[{ required: true, message: 'Bitte wählen Sie einen Advert aus' }]}
          >
            <Select placeholder="Advert auswählen">
              <Option value="advert1">Advert 1 - Haus am See</Option>
              <Option value="advert2">Advert 2 - Ferienwohnung</Option>
              <Option value="advert3">Advert 3 - Pension</Option>
            </Select>
          </Form.Item>
          
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                Zuweisen
              </Button>
              <Button onClick={() => setAssignmentModalVisible(false)}>
                Abbrechen
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}; 